import { TextField, Box } from '@mui/material';
import { LocalizationProvider, DatePicker as MuiDatePicker, StaticDatePicker as MuiStaticDatePicker, PickersDay, CalendarPickerSkeleton } from '@mui/lab';
import { isMobile } from 'react-device-detect';
import { useSelector } from "react-redux";
import { getData } from "../../api/dataSlice";
import { useTranslation } from 'react-i18next';
import { LenientDayjsAdapter } from '../Utilities';
import i18next from 'i18next';
import dayjs from 'dayjs';

export default function DatePicker({
    timestamp,
    setTimestamp,
    loading,
    shouldDisableDate,
    pt = 0,
    pb = 0,
    extended = true,
    withoutToday = false,
    customRender = null,
    open,
    setOpen,
    minDate: minDateProp = null,
    maxDate: maxDateProp = null,
    onSelected = null
}) {

    const { source, siderWidth } = useSelector(getData)
    const { t } = useTranslation()

    const daySize = 34
    const fallbackMinDate = dayjs(source?.[0]?.[0]?.[0])
    const fallbackMaxDate = dayjs(source?.[0]?.[source?.[0]?.length - 1]?.[0])
    const minDate = minDateProp ?? (fallbackMinDate.isValid() ? fallbackMinDate : null)
    const maxDate = maxDateProp ?? (fallbackMaxDate.isValid() ? fallbackMaxDate : null)
    const today = dayjs()
    const isControlledOpen = typeof open === "boolean" && typeof setOpen === "function"
    
    const value = (() => {
        if (!timestamp) return null
        const d = dayjs(timestamp)
        return d.isValid() ? d : null
    })()

    const disableTodayWrapper = (date) => {
        const isToday = dayjs(date).isSame(today, 'day');
        const isDisabledByProp = shouldDisableDate?.(date) ?? false;
        return (withoutToday && isToday) || isDisabledByProp;
    }

    const onChange = (value) => {
        // skip MUI's mount-time onChange that normalizes value's time to midnight on the same day (not user-driven)
        const current = timestamp ? dayjs(timestamp) : null
        const isSpuriousNormalize = current?.isValid?.()
            && value?.isValid?.()
            && current.isSame(value, 'day')
            && value.hour() === 0 && value.minute() === 0
            && (current.hour() !== 0 || current.minute() !== 0)
        if (isSpuriousNormalize) return

        setTimestamp(value)
        if (isControlledOpen) setOpen(false)
        if (typeof onSelected === "function") onSelected(value)
    }

    return (
        <LocalizationProvider
            dateAdapter={LenientDayjsAdapter}
            locale={i18next.language}
        >
            {isMobile ? (
                <Box sx={{ pb, pt, width: "100%", display: "flex", justifyContent: "center" }}>
                    <MuiDatePicker
                        label={t("date.date")}
                        inputFormat={(t("date.format.full"))}
                        mask={t("date.mask.full")}
                        disableMaskedInput
                        renderInput={props => customRender ? customRender(props) : <TextField {...props} fullWidth />}
                        value={value}
                        onChange={onChange}
                        onAccept={onChange}
                        cancelText={t("actions.cancel")}
                        minDate={minDate}
                        maxDate={maxDate}
                        loading={loading}
                        shouldDisableDate={disableTodayWrapper}
                        renderLoading={() => <CalendarPickerSkeleton />}
                        showTodayButton={!withoutToday}
                        todayText={t("date.today")}
                        showToolbar={false}
                         {...(isControlledOpen && {
                            open: open,
                            onOpen: () => setOpen(true),
                            onClose: () => setOpen(false)
                        })}
                    />
                </Box>
            ) : (
                <Box sx={{
                    "& > div": {
                        minWidth: siderWidth,
                        pb,
                        pt
                    },
                    "& > div > div": {
                        border: "1px solid",
                        borderColor: "#BDBDBD",
                        borderRadius: "4px"
                    },
                    "& > div > div, & > div > div > div, & > div > div > div > div": {
                        width: extended ? siderWidth - 2 : siderWidth
                    },
                    "& .MuiTypography-caption": {
                        width: daySize + 4,
                        margin: 0
                    },
                    "& .PrivatePickersSlideTransition-root": {
                        minHeight: daySize * 6.3,
                        overflow: "hidden"
                    },
                    '& .PrivatePickersSlideTransition-root [role="row"]': {
                        margin: 0
                    },
                    "& .MuiPickersDay-root": {
                        width: daySize,
                        height: daySize
                    },
                    "& .MuiYearPicker-root": {
                        justifyContent: "center"
                    }
                }}>
                    <MuiStaticDatePicker
                        displayStaticWrapperAs="desktop"
                        openTo="day"
                        renderInput={() => {}}
                        value={value}
                        onChange={onChange}
                        renderDay={(day, _value, DayComponentProps) => <PickersDay {...DayComponentProps} />}
                        minDate={minDate}
                        maxDate={maxDate}
                        loading={loading}
                        shouldDisableDate={disableTodayWrapper}
                        renderLoading={() => <CalendarPickerSkeleton />}
                    />
                </Box>
            )}
        </LocalizationProvider>
    )
}